import { Dropdown, Image, List, Menu, Popover, Space, Tabs } from "antd";
import { differenceInDays, differenceInHours, format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { vi } from 'date-fns/locale';
import RegisterModal from "../../../modules/register/Index";
import LoginModal from "../../../modules/login/Index";
import { useAppDispatch, useAppSelector } from "../../../redux/Hooks";
import { logout, setChangePassWarning, setUser, toggleLoginModal } from "../../../modules/login/redux/LoginSlice";
import { toggleRegisterModal } from "../../../modules/register/redux/RegisterSlice";
import ForgotPassword from "../../../modules/forgot-password/Index";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as Bell } from '../../../assets/images/bell.svg';
import { useLazyGetUserInfoQuery, useLogOutMutation } from "../../../redux/api/AccountApi";
import { ChevronDownIcon } from "@fluentui/react-icons-mdl2";
import ChangePassword from "../../../modules/profile/change-password/Index";
import { NotificationResponse, NotificationTypes } from "../../../redux/api/apiTypes";
import { useLazyCountNotificationNotReadQuery, useLazyGetAllNotificationQuery, useLazyReadNotificationQuery } from "../../../redux/api/DashboardApi";
import { UserOutlined } from "@ant-design/icons";
import { ContactResponse } from "../../../modules/contact-us/types";


const HeaderInfo = () => {
    const [currentTime, setCurrentTime] = useState(format(new Date(), "HH:mm:ss dd/MM/yyyy", { locale: vi }));
    const loginState = useAppSelector(state => state.login);
    const registerState = useAppSelector(state => state.register);
    const forgotPasswordState = useAppSelector(state => state.forgotPassword);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [displayName, setDisplayName] = useState("");
    const [changePassVisible, setChangePassVisible] = useState(false);
    const [productData, setProductData] = useState<NotificationResponse[]>([]);
    const [auctionData, setAuctionData] = useState<NotificationResponse[]>([]);
    const [page, setPage] = useState(0);
    const [activeKey, setActiveKey] = useState(NotificationTypes.TAISAN);
    const [loggedOut, setLoggedOut] = useState(false);

    const [trigger] = useLazyGetUserInfoQuery();
    const [notificationTrigger, notificationResponse] = useLazyGetAllNotificationQuery();
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [logOut] = useLogOutMutation();
    const [countNotification, countNotificationResponse] = useLazyCountNotificationNotReadQuery();
    const [readNotification] = useLazyReadNotificationQuery();
    const [headquarter, setHeadquarter] = useState<ContactResponse>();

    const firstLoad = useRef(true);

    useEffect(() => {
        if (loginState.contacts.content.length) {
            setHeadquarter(loginState.contacts.content.find(item => item.type === 0))
        }
    }, [loginState.contacts.content])

    useEffect(() => {
        if (loggedOut) {
            dispatch(toggleLoginModal())
        }
    }, [loggedOut, dispatch])

    useEffect(() => {
        if (popoverVisible && firstLoad.current) {
            notificationTrigger({ type: NotificationTypes.TAISAN }).unwrap().then(res => {
                firstLoad.current = false;
                if (activeKey === NotificationTypes.TAISAN) {
                    setProductData(res.content);
                }
            })
        }
    }, [popoverVisible, notificationTrigger, activeKey]);

    useEffect(() => {
        if (!Object.keys(loginState.user).length && loginState.userToken) {
            trigger({}).unwrap().then(res => {
                setLoggedOut(false);
                dispatch(setUser(res));
            }).catch(err => {
                setLoggedOut(true);
                dispatch(logout());
                navigate("/")
            })
        }

        if (Object.keys(loginState.user).length) {
            let newDisplayName = "";
            if (loginState.user.fullName) {
                let userName = loginState.user.fullName;
                newDisplayName = userName.length > 20 ? userName.slice(0, 20) + "..." : userName;
            }
            setDisplayName(newDisplayName);
            countNotification({});
        }
    }, [loginState, trigger, dispatch, countNotification])

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(format(new Date(), "HH:mm:ss cccc, dd/MM/yyyy", { locale: vi }));
        }, 1000)

        return () => clearInterval(interval);
    }, [])

    useEffect(() => {
        // Kiểm tra thời gian thay đổi mật khẩu gần nhất
        if (loginState.user?.changePassDate) {
            let timeDiff = differenceInDays(new Date(), loginState.user.changePassDate);
            if (timeDiff >= 90) {
                dispatch(setChangePassWarning(true));
            }
        }
    }, [loginState.user?.changePassDate, dispatch])

    const onLogoutClick = () => {
        // localStorage.setItem("isLogin", "false");
        logOut({}).then(() => {
            setLoggedOut(true);
            dispatch(logout());
            navigate("/")
        }).catch(err => {
            console.log(err.data?.message || "Đăng xuất thất bại");
        })
    }

    const toggleChangePassVisible = () => {
        setChangePassVisible(!changePassVisible);
    }

    const getDropdownMenu = () => {
        return (
            <Menu>
                <Menu.Item key="account">
                    <Link to="/profile/detail">
                        Tài khoản
                    </Link>
                </Menu.Item>
                <Menu.Item key="change-password" onClick={toggleChangePassVisible}>
                    Đổi mật khẩu
                </Menu.Item>
                <Menu.Item key="interest">
                    <Link to="profile/product-care">
                        Tài sản quan tâm
                    </Link>
                </Menu.Item>
                <Menu.Item key="auction-history">
                    <Link to="/profile/auction-history">
                        Lịch sử tham gia đấu giá
                    </Link>
                </Menu.Item>
                {/* <Menu.Item key="complete-profile">
                    Hoàn thiện hồ sơ đấu giá
                </Menu.Item> */}
                <Menu.Item key="sell-history">
                    <Link to="/profile/auction-register-history">
                        Lịch sử bán tài sản
                    </Link>
                </Menu.Item>
                <Menu.Item key="notification">
                    <Link to='/profile/prepay-list'>
                        Theo dõi thông tin giao dịch tiền đặt trước
                    </Link>
                </Menu.Item>
                <Menu.Item key="logout" onClick={onLogoutClick}>
                    Đăng xuất
                </Menu.Item>
            </Menu>
        )
    }

    const onTabChange = useCallback((key: string) => {
        setPage(0);
        if (key === NotificationTypes.TAISAN) {
            setActiveKey(NotificationTypes.TAISAN);
            notificationTrigger({ type: NotificationTypes.TAISAN }).unwrap().then(res => {
                setProductData(res.content);
            });
        } else if (key === NotificationTypes.DIENBIEN) {
            setActiveKey(NotificationTypes.DIENBIEN);
            notificationTrigger({ type: NotificationTypes.DIENBIEN }).unwrap().then(res => {
                setAuctionData(res.content);
            });
        }
    }, [notificationTrigger]);

    // Hiển thị nội dung thông báo
    const getPopoverContent = useMemo(() => {
        // Kiểm tra scroll
        const checkScroll = (e: React.WheelEvent<HTMLDivElement>) => {
            if (e.currentTarget.scrollHeight - e.currentTarget.scrollTop <= (e.currentTarget.clientHeight + 50) && !notificationResponse.isLoading) {
                let currentActiveKey = activeKey;
                notificationTrigger({ type: currentActiveKey, page: page + 1 }).unwrap().then(res => {
                    if (currentActiveKey === NotificationTypes.TAISAN) {
                        setProductData(productData.concat(res.content));
                    } else if (currentActiveKey === NotificationTypes.DIENBIEN) {
                        setAuctionData(auctionData.concat(res.content));
                    }
                    setPage(page + 1);
                })
            }
        }

        // Hiển thị danh sách
        const listContent = (type: string) => {
            return (
                <div onScroll={checkScroll} style={{ maxHeight: 400, overflow: 'auto' }} className="notification-scroll">
                    <List
                        loading={notificationResponse.isLoading || notificationResponse.isFetching}
                        dataSource={type === NotificationTypes.TAISAN ? productData : auctionData}
                        renderItem={item => {
                            let timeDiff = 0;
                            let timeDiffStr = "";

                            if (item.modifiedDate) {
                                timeDiff = Math.abs(differenceInHours(item.modifiedDate, new Date().getTime()));

                                if (timeDiff < 1) {
                                    timeDiffStr = "< 1h";
                                } else if (timeDiff >= 1 && timeDiff < 24) {
                                    timeDiffStr = `${timeDiff}h`;
                                } else {
                                    timeDiffStr = format(item.modifiedDate, "dd/MM/yyyy");
                                }
                            }

                            let avatar = item.vbAttachmentImages?.find(item => item.storageType === "avatar") || item.vbAttachmentImages?.[0];

                            return (
                                <List.Item
                                    className={item.read ? "notification-read" : ""}
                                    onClick={() => {
                                        setPopoverVisible(false);
                                        readNotification(item.id).unwrap().then(() => {
                                            navigate(`/auction-item/${item.productId || 0}/${item.statuteId || 0}`);
                                        })
                                    }}>
                                    <List.Item.Meta
                                        // style={{ alignItems: 'center' }}
                                        avatar={avatar && avatar.attachmentId ?
                                            <Image src={`${process.env.REACT_APP_API_URL}/files/downloadFile/${avatar.attachmentId}`} height={80} width={80} preview={false} /> :
                                            <UserOutlined style={{ fontSize: 40 }} />}
                                        // avatar={<Image src={`${process.env.REACT_APP_API_URL}/files/downloadFile/${}`} />}
                                        title={<span style={{ color: "#E8AA0C" }}>{item.productName}</span>}
                                        description={item.description}
                                    />
                                    <div>{timeDiffStr}</div>
                                </List.Item>)
                        }}
                    />
                </div>
            )
        }

        if (popoverVisible) {

            return (
                <>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>Thông báo</div>
                    <Tabs defaultActiveKey={NotificationTypes.TAISAN} onChange={onTabChange}>
                        <Tabs.TabPane tab="Tài sản" key={NotificationTypes.TAISAN} style={{ width: 500 }}>
                            {listContent(NotificationTypes.TAISAN)}
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Diễn biến đấu giá" key={NotificationTypes.DIENBIEN} style={{ width: 500 }}>
                            {listContent(NotificationTypes.DIENBIEN)}
                        </Tabs.TabPane>
                    </Tabs>

                </>
            )
        }

        return <></>;
    }, [popoverVisible, notificationResponse.isLoading, notificationResponse.isFetching, activeKey, notificationTrigger,
        page, productData, auctionData, readNotification, navigate, onTabChange]);

    return (
        <div className="time-header">
            <Space size={25}>
                <span className="font-size-12">Hà Nội, Việt Nam - {currentTime}</span>
                {headquarter?.mobile &&
                    <span>Hotline: <a href={`tel:${headquarter.mobile}`} className="font-size-12">{headquarter.mobile}</a></span>
                }
                {headquarter?.email &&
                    <span>Email: <a href={`mailto:${headquarter.email}`} className="font-size-12">{headquarter.email}</a></span>
                }
            </Space>
            <Space size={25}>
                {!Object.keys(loginState.user).length && !loginState.userToken ?
                    <>
                        {/* <span className="font-size-12 font-bold letter-spacing-big cursor-pointer" onClick={() => dispatch(toggleRegisterModal())}>THÔNG TIN TÀI KHOẢN</span> */}
                        <span
                            className="font-size-12 font-bold letter-spacing-big cursor-pointer"
                            onClick={() => dispatch(toggleRegisterModal())}
                            style={{ color: "#222" }}>ĐĂNG KÝ</span>
                        {/* <span className="font-size-12 font-bold letter-spacing-big cursor-pointer" onClick={() => navigate('/profile')}>HỒ SƠ</span> */}
                        <span
                            className="font-size-12 font-bold letter-spacing-big cursor-pointer"
                            onClick={() => dispatch(toggleLoginModal())}
                            style={{ color: "#222" }}>ĐĂNG NHẬP</span>
                    </> :
                    <>
                        <Popover
                            visible={popoverVisible}
                            onVisibleChange={(visible) => setPopoverVisible(visible)}
                            trigger="click"
                            content={getPopoverContent}
                            overlayClassName="notification-popover"
                            placement="bottomRight">
                            <div style={{ display: 'flex', cursor: 'pointer' }}>
                                <Bell />
                                <div className="header-notification">
                                    {countNotificationResponse?.data || 0}
                                </div>
                            </div>
                        </Popover>
                        <Dropdown overlay={getDropdownMenu} className="cursor-pointer" overlayClassName="custom-dropdown">
                            <span className="font-size-12" style={{ color: "#222" }}>
                                Xin chào, <span className="font-bold">{displayName}</span>
                                <ChevronDownIcon style={{ marginLeft: 10 }} />
                            </span>
                        </Dropdown>
                    </>}
            </Space>
            {registerState.modalVisible &&
                <RegisterModal />}
            {loginState.modalVisible &&
                <LoginModal />}
            {forgotPasswordState.modalVisible &&
                <ForgotPassword />}

            {(changePassVisible || loginState.changePassWarning) &&
                <ChangePassword visible={changePassVisible || loginState.changePassWarning} onCancel={toggleChangePassVisible} />}
        </div>
    )
}

export default HeaderInfo;